import { fetchVerification } from '@afterpaytouch/event-verify'
import { portalAction, PortalThunk } from '../types'
import {
  AddEmailToRegistrationRequest,
  AddMobileToRegistrationRequest,
  ConfirmRegistrationRequest,
  Email,
  Mobile,
  RegistrationData,
  RegistrationId,
  VerifyRegistrationAuthorizationRequest,
  VerifyRegistrationMobileEventRequest,
  VerifyRegistrationSMSAuthorizationResponse,
} from '../../model/registration'
// TODO use the types from the packages/registration package
// from '@afterpaytouch/registration'
import { verifyServiceApi } from '../api'
import { FinaliseAccountRequest } from '@afterpaytouch/portal-api'
import { VERIFY_SERVICE_BASE_URL } from '../request'

/**
 * TODO: Add proper registration state selectors
 */
export const verifyRegistrationSMSAuthorization: PortalThunk<void, VerifyRegistrationSMSAuthorizationResponse> = portalAction(
  'registration/verify/mobile/sms/authz',
  async (_, thunk) => {
    // TODO: Get these values from proper selectors when registration state is implemented
    const registrationId = 'TODO-get-from-selector' // registrationSelector(thunk.getState())
    const portalToken = 'TODO-get-from-selector' // tokenSelector(thunk.getState())
    const amplitudeSessionId = (await import('amplitude-js')).default.getInstance()?.getSessionId() || 0

    // TODO: Get consumer data from registration state
    const consumerData: RegistrationData = {
      // TODO: Replace with actual data from registration state
      address1: '',
      area2: '',
      countryCode: 'AU' as any,
      dateOfBirth: '',
      givenNames: '',
      mobile: '',
      email: '',
      otherGivenNames: '',
      postcode: '',
      suburb: '',
      surname: '',
      preferredLocale: 'en-AU' as any,
    }

    const request: VerifyRegistrationAuthorizationRequest = {
      registrationId: registrationId ?? '',
      portalToken,
      amplitudeSessionId,
      consumer: consumerData,
    }

    try {
      const result = await verifyServiceApi
        .post<VerifyRegistrationAuthorizationRequest, VerifyRegistrationSMSAuthorizationResponse>('/authz/sms', request)
        .catch((e) => {
          return {
            code: e.response.data.code,
            message: e.response.data.message,
          }
        })
      return result
    } catch (e: any) {
      return {
        code: e?.response?.data?.code || ('FAILED' as any),
        message: e?.response?.data?.message || ('UNKNOWN' as any),
      }
    }
  }
)

/**
 * Simplified version of verifyRegistrationMobileSMSEvent from checkout
 * TODO: Add proper transaction token selector
 */
export const verifyRegistrationMobileSMSEvent: PortalThunk<VerifyRegistrationMobileEventRequest, void> = portalAction(
  'registration/verify/mobile/sms/delay',
  async (verifyRegistrationMobileEventRequest, thunk) => {
    const portalToken = 'TODO-get-from-selector' // tokenSelector(thunk.getState())
    const { onSuccess, onWaiting, onError } = verifyRegistrationMobileEventRequest

    return await fetchVerification({
      url: encodeURI(`${VERIFY_SERVICE_BASE_URL}/sms/${portalToken}`),
      onSuccess: () => {
        onSuccess()
      },
      onError: (d) => {
        onError(d)
      },
      onWaiting: (d) => {
        onWaiting(d)
      },
    })
  }
)

/**
 * Confirm mobile registration with SMS code - copied from checkout
 * TODO: Add proper registration state selector for registrationId and mobile
 */
export const confirmRegistrationMobile: PortalThunk<string, void> = portalAction('registration/mobile', async (smsCode, thunk) => {
  // TODO: Get these values from proper selectors when registration state is implemented
  const registrationId: RegistrationId = 'TODO-get-from-selector' // registrationSelector(thunk.getState()).id
  const mobile: Mobile = 'TODO-get-from-selector' // registrationSelector(thunk.getState()).data.mobile

  const request: AddMobileToRegistrationRequest = {
    registrationId,
    mobile,
    smsCode,
  }

  return await thunk.extra.portalApi().post('/portal/consumers/registrations/mobile', request)
})

/**
 * Confirm email registration with email code - copied from checkout
 * TODO: Add proper email and registration state selectors
 */
export const confirmRegistrationEmail: PortalThunk<string, void> = portalAction('registration/email', async (emailCode, thunk) => {
  // TODO: Get these values from proper selectors when registration state is implemented
  const email: Email = 'TODO-get-from-selector' // emailSelector(thunk.getState())
  const registrationId: RegistrationId = 'TODO-get-from-selector' // registrationSelector(thunk.getState()).id

  const request: AddEmailToRegistrationRequest = {
    registrationId,
    email,
    emailCode,
  }

  return await thunk.extra.portalApi().post('/portal/consumers/registrations/email', request)
})

/**
 * Confirm registration - copied from checkout
 * TODO: Add proper registration state selectors for registrationId, preferredLocale, marketingOptIn
 */
export const confirmRegistration: PortalThunk<void, void> = portalAction('registration/confirm', async (_, thunk) => {
  // TODO: Get these values from proper selectors when registration state is implemented
  const registrationId: RegistrationId = 'TODO-get-from-selector' // registrationSelector(thunk.getState()).id
  const preferredLocale = 'en-AU' as any // registrationSelector(thunk.getState()).data.preferredLocale
  const marketingOptIn: boolean | null = null // registrationSelector(thunk.getState()).data.marketingOptIn

  const request: ConfirmRegistrationRequest = {
    registrationId,
    origin: 'CONSUMER_PORTAL',
    preferredLocale,
    ...(marketingOptIn !== null ? { marketingPreference: marketingOptIn } : {}),
  }

  return await thunk.extra.portalApi().post('/portal/consumers/registrations/confirm', request)
})

/**
 * Finalise account details - copied from checkout
 * TODO: Add proper registration state selector for consumer data
 */
export const finaliseAccountDetails: PortalThunk<void, void> = portalAction('registration/finaliseAccount', async (_, thunk) => {
  // TODO: Get consumer data from proper selectors when registration state is implemented
  const registrationData: RegistrationData = {
    // TODO: Replace with actual data from registration state
    address1: 'TODO-get-from-selector',
    area2: 'TODO-get-from-selector',
    countryCode: 'AU' as any,
    dateOfBirth: 'TODO-get-from-selector',
    givenNames: 'TODO-get-from-selector',
    mobile: 'TODO-get-from-selector',
    email: 'TODO-get-from-selector',
    otherGivenNames: 'TODO-get-from-selector',
    postcode: 'TODO-get-from-selector',
    suburb: 'TODO-get-from-selector',
    surname: 'TODO-get-from-selector',
    preferredLocale: 'en-AU' as any,
  }

  const { preferredLocale, ...finaliseAccountData } = registrationData

  const request: FinaliseAccountRequest = {
    ...finaliseAccountData,
    creditPolicyAgree: false,
    providedCreditCheckConsent: finaliseAccountData.countryCode === 'NZ',
  }

  return await thunk.extra.portalApi().post('/portal/consumers/account/account-details', request)
})
